"use client";

import Link from "next/link";
import { useState } from "react";
import { PF_BTN_ACCENT, PF_BTN_INK, PF_BTN_PAPER } from "@/components/platform/cta";
import { PLATFORM_SUPPORT_EMAIL } from "@/lib/platform-brand";
import type { PlanVM, VerticalData } from "@/lib/platform-pricing";

// Pricing block for the marketing landing ("COMMERCE, PRINTED."). Client
// island only because of the Retail/Café toggle — both verticals' view-models
// are built on the server (real `publicPlans` data) and passed in, so the
// switch is instant with no refetch. Prices, plan names and feature lists are
// all live data; nothing here is hardcoded marketing fiction.

const CONTACT_HREF = `mailto:${PLATFORM_SUPPORT_EMAIL}`;

export function PricingSection({ retail, cafe }: { retail: VerticalData; cafe: VerticalData }) {
  const [key, setKey] = useState<"contractor" | "cafe">("contractor");
  const active = key === "contractor" ? retail : cafe;

  return (
    <section
      id="pricing"
      style={{
        background: "var(--pf-paper)",
        borderTop: "2px solid var(--pf-ink)",
        padding: "clamp(60px,8vw,100px) 0",
      }}
    >
      <div className="pf-ct">
        <div style={{ textAlign: "center", maxWidth: 700, margin: "0 auto 36px" }}>
          <p className="pf-eyebrow" style={{ margin: "0 0 16px" }}>
            Pricing — read live from the billing system
          </p>
          <h2 className="pf-h" style={{ fontSize: "clamp(40px,5.6vw,76px)" }}>
            The price tag.
          </h2>
          <p
            style={{
              fontSize: 15.5,
              lineHeight: 1.7,
              margin: "20px 0 0",
              color: "color-mix(in oklab, var(--pf-ink), transparent 25%)",
            }}
          >
            Same tiers for every business — a café starts with ordering, a counter and a kitchen; a
            shop starts with the full commerce kit. These numbers come from the same table checkout
            charges: what you see is what you pay.
          </p>
        </div>

        {/* Vertical toggle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 44 }}>
          <div
            style={{
              display: "inline-flex",
              flexWrap: "wrap",
              border: "2px solid var(--pf-ink)",
              background: "var(--pf-white)",
            }}
          >
            <ToggleButton on={key === "contractor"} onClick={() => setKey("contractor")}>
              {retail.label}
            </ToggleButton>
            <ToggleButton on={key === "cafe"} onClick={() => setKey("cafe")}>
              {cafe.label}
            </ToggleButton>
          </div>
        </div>

        {/* Featured Starter receipt */}
        {active.starter && (
          <div className="pf-receipt-wrap" style={{ marginBottom: "clamp(28px,4vw,48px)" }}>
            <div className="pf-receipt pf-starter-grid" style={{ padding: 0 }}>
              <div
                className="pf-starter-side"
                style={{
                  padding: "36px 34px 42px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "2.2px",
                    textTransform: "uppercase",
                    margin: "0 0 10px",
                    color: "var(--pf-accent-down)",
                  }}
                >
                  Start here
                </p>
                <h3 className="pf-h" style={{ fontSize: 40 }}>
                  {active.starter.name}
                </h3>
                <div
                  style={{ margin: "16px 0 0", display: "flex", alignItems: "baseline", gap: 6 }}
                >
                  <span className="pf-h" style={{ fontSize: "clamp(56px,6vw,76px)" }}>
                    {active.starter.priceLabel}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>/mo</span>
                </div>
                <p
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 14,
                    lineHeight: 1.7,
                    margin: "18px 0 0",
                    color: "color-mix(in oklab, var(--pf-ink), transparent 28%)",
                  }}
                >
                  {active.desc}
                </p>
                {active.starter.href && (
                  <Link
                    href={active.starter.href}
                    className={PF_BTN_INK}
                    style={{ marginTop: 30, alignSelf: "flex-start" }}
                  >
                    Open your store →
                  </Link>
                )}
              </div>
              <div style={{ padding: "36px 34px 42px" }}>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "2.2px",
                    textTransform: "uppercase",
                    margin: "0 0 20px",
                    color: "color-mix(in oklab, var(--pf-ink), transparent 38%)",
                  }}
                >
                  Printed on every {active.starter.name} receipt:
                </p>
                <div className="pf-feat-grid">
                  {active.starter.features.map((f) => (
                    <FeatRow key={f} label={f} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tier stubs (Pro / Max / Enterprise) */}
        <div className="pf-tiers">
          {active.tiers.map((tier, i) => (
            <TierCard key={tier.code} tier={tier} popular={i === 0} />
          ))}
        </div>

        <p
          className="pf-mono"
          style={{
            textAlign: "center",
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "1.8px",
            textTransform: "uppercase",
            color: "color-mix(in oklab, var(--pf-ink), transparent 38%)",
            margin: "36px 0 0",
          }}
        >
          All plans: subdomain + Stripe checkout + SSL · no setup fees · cancel anytime
        </p>
      </div>
    </section>
  );
}

function ToggleButton({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className="pf-mono"
      style={{
        border: "none",
        cursor: "pointer",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: 11.5,
        letterSpacing: "1.8px",
        padding: "14px 26px",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        transition: "background 150ms ease, color 150ms ease",
        background: on ? "var(--pf-ink)" : "transparent",
        color: on ? "var(--pf-on-ink)" : "color-mix(in oklab, var(--pf-ink), transparent 30%)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          background: on ? "var(--pf-accent)" : "transparent",
          border: on ? "none" : "1.5px solid currentColor",
          display: "inline-block",
        }}
      />
      {children}
    </button>
  );
}

/** Dotted-leader feature line: LABEL ........ ✓ */
function FeatRow({ label, onDark }: { label: string; onDark?: boolean }) {
  return (
    <div
      className="pf-lrow"
      style={{
        fontSize: 12,
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        padding: "4px 0",
        color: onDark ? "rgba(241,237,225,0.88)" : undefined,
      }}
    >
      <span>{label}</span>
      <span
        className="pf-dots"
        style={onDark ? { borderColor: "rgba(241,237,225,0.3)" } : undefined}
      />
      <span style={{ color: "var(--pf-accent)" }}>✓</span>
    </div>
  );
}

function TierCard({ tier, popular }: { tier: PlanVM; popular: boolean }) {
  return (
    <div
      className={popular ? "pf-dark" : undefined}
      style={{
        background: popular ? "var(--pf-ink)" : "var(--pf-white)",
        color: popular ? "var(--pf-on-ink)" : undefined,
        border: "2px solid var(--pf-ink)",
        boxShadow: "var(--pf-shadow)",
        padding: "32px 30px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {popular && (
        <span className="pf-stamp" style={{ position: "absolute", top: -16, right: 14 }}>
          Most popular
        </span>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h3 className="pf-h" style={{ fontSize: 30, color: popular ? "#fff" : undefined }}>
          {tier.name}
        </h3>
        <span
          className="pf-h"
          style={{
            fontSize: tier.isContactUs ? 26 : 34,
            color: popular ? "#fff" : undefined,
            whiteSpace: "nowrap",
          }}
        >
          {tier.priceLabel}
          {!tier.isContactUs && (
            <span className="pf-mono" style={{ fontSize: 12, fontWeight: 700 }}>
              /mo
            </span>
          )}
        </span>
      </div>
      {tier.plusLabel && (
        <p
          className="pf-mono"
          style={{
            fontSize: 11,
            letterSpacing: "1.6px",
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--pf-accent)",
            margin: "20px 0 12px",
          }}
        >
          + {tier.plusLabel}
        </p>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          marginTop: tier.plusLabel ? 0 : 20,
        }}
      >
        {tier.features.map((f) => (
          <FeatRow key={f} label={f} onDark={popular} />
        ))}
      </div>
      {tier.href ? (
        <Link
          href={tier.href}
          className={popular ? PF_BTN_ACCENT : PF_BTN_PAPER}
          style={{ marginTop: 28, padding: "15px 24px", fontSize: 11.5 }}
        >
          Open your store →
        </Link>
      ) : (
        <a
          href={CONTACT_HREF}
          className={popular ? PF_BTN_ACCENT : PF_BTN_PAPER}
          style={{ marginTop: 28, padding: "15px 24px", fontSize: 11.5 }}
        >
          Contact us
        </a>
      )}
    </div>
  );
}
